'use client'

import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent } from 'react'
import { flushSync } from 'react-dom'
import { Button } from '@/components/ui/button'
import { emptyOrderValuesFor, type OrderFormValues } from '@/components/santa-video/order-form-body'
import { useOnBfcacheRestore } from '@/components/santa-video/order-form-lifecycle'
import { loadDraft, saveDraft } from '@/components/santa-video/wizard/order-draft'
import { useCheckoutSubmit } from '@/components/santa-video/wizard/use-checkout-submit'
import { useWizardAnalytics } from '@/components/santa-video/wizard/use-wizard-analytics'
import { BookKnowledgeFields, ChildFields, DeliveryFields, KnowledgeFields, type FieldSetter, type StepFieldsProps } from '@/components/santa-video/wizard/wizard-step-fields'
import { LAST_STEP, STEP_KEYS, validateStep, type StepError, type WizardStep } from '@/components/santa-video/wizard/wizard-steps'
import type { BookOrderFormCopy, OrderFormCopy } from '@/lib/santa-video/order-page-copy'
import type { VideoLocale } from '@/server/domain/santa-video/locale'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

/**
 * Qué vídeo se pide, con SU copy: sin `story`, la carta (el wizard de
 * siempre); el libro trae la copy de sus campos propios (el reto).
 */
type StoryProps = { story?: 'letter'; copy: OrderFormCopy } | { story: 'book'; copy: BookOrderFormCopy }

type VideoOrderWizardProps = StoryProps & {
  /** «9,90 €», ya formateado en el servidor. Va en el resumen, no en el botón. */
  priceLabel: string
  locale: VideoLocale
  privacyHref: string
  /** Inyectable para los tests; en producción, el checkout alojado de Stripe. */
  navigate?: (url: string) => void
}

const noopSubscribe = () => () => {}

function goToCheckout(url: string) {
  window.location.href = url
}

/**
 * La isla de cliente de `/video-papa-noel/crear` (spec 2026-09-23 §6). La
 * validación de verdad es la del servidor: aquí sólo el mínimo por paso y los
 * `maxLength` del dominio, y cualquier 4xx/5xx se enseña con su `message`.
 *
 * El borrador sólo existe en el navegador, así que el primer render (el de la
 * hidratación) tiene que ser el vacío del servidor. `useSyncExternalStore`
 * dice «ya estamos en el cliente» sin un `setState` en un efecto, y el cambio
 * de `key` remonta el formulario leyendo el borrador en su inicializador.
 */
export function VideoOrderWizard(props: VideoOrderWizardProps) {
  const hydrated = useSyncExternalStore(noopSubscribe, () => true, () => false)
  return <WizardForm key={hydrated ? 'client' : 'server'} hydrated={hydrated} {...props} />
}

function initialValues(hydrated: boolean, story: VideoStoryId): OrderFormValues {
  return hydrated ? loadDraft(story) : emptyOrderValuesFor(story)
}

/**
 * Los campos del paso 2, los de la historia del pedido. Estrechar por `story`
 * estrecha la copy, y el `switch` es exhaustivo: una historia nueva no compila
 * hasta tener aquí sus campos, en vez de pintar en silencio los de la carta.
 */
function KnowledgeStep({ storyProps, values, set }: { storyProps: StoryProps; values: OrderFormValues; set: FieldSetter }) {
  switch (storyProps.story) {
    case 'book': return <BookKnowledgeFields copy={storyProps.copy} set={set} values={values} />
    case 'letter':
    case undefined: return <KnowledgeFields copy={storyProps.copy} set={set} values={values} />
    default: {
      const unhandled: never = storyProps
      return unhandled
    }
  }
}

function WizardForm(props: VideoOrderWizardProps & { hydrated: boolean }) {
  const { copy, priceLabel, locale, privacyHref, navigate = goToCheckout, hydrated } = props
  const story = props.story ?? 'letter'
  const [values, setValues] = useState<OrderFormValues>(() => initialValues(hydrated, story))
  const [step, setStep] = useState<WizardStep>(0)
  const [stepError, setStepError] = useState<StepError | null>(null)
  const [consent, setConsent] = useState(false)
  const checkout = useCheckoutSubmit({ locale, story, networkError: copy.networkError, navigate })
  const heading = useRef<HTMLHeadingElement>(null)
  const analytics = useWizardAnalytics({ step, locale, story, hydrated })
  // Atrás desde Stripe restaura la página del bfcache con la casilla marcada:
  // el consentimiento se da cada vez que se va a pagar, no se hereda (spec §6).
  useOnBfcacheRestore(() => setConsent(false))

  useEffect(() => {
    if (hydrated) saveDraft(values, story)
  }, [hydrated, values, story])

  /** Escribir en un campo retira el aviso del paso: ya no describe lo que hay en pantalla. */
  const set: FieldSetter = (key) => (value) => {
    setStepError(null)
    setValues((current) => ({ ...current, [key]: value }))
  }

  /**
   * El foco al título del paso nuevo va aquí, en la respuesta al clic, y no en
   * un efecto: un efecto con bandera de «montado» enfocaba en el montaje bajo el
   * StrictMode de dev. `flushSync` deja el título nuevo ya pintado cuando recibe
   * el foco, así el lector de pantalla anuncia el paso nuevo y no el anterior.
   */
  function goTo(next: WizardStep) {
    flushSync(() => {
      setStepError(null)
      // Salir del paso 3 retira el consentimiento: hay que volver a darlo al
      // volver, y así un doble clic en «Siguiente» del paso 2 no puede caer en
      // un botón de pago ya habilitado.
      if (step === LAST_STEP) setConsent(false)
      setStep(next)
    })
    heading.current?.focus()
  }

  /** Enter en cualquier input cae aquí: en los pasos 1-2 avanza, sólo el 3 paga. */
  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (step === LAST_STEP) return void checkout.submit(values)
    const problem = validateStep(step, values, story)
    if (problem) {
      analytics.stepErrorShown(problem)
      return setStepError(problem)
    }
    analytics.stepCompleted()
    goTo((step + 1) as WizardStep)
  }

  const stepCopy = copy.steps[STEP_KEYS[step]]
  const onLastStep = step === LAST_STEP

  return (
    <form onPointerDownCapture={analytics.formStarted} onKeyDownCapture={analytics.formStarted} onChangeCapture={analytics.formStarted} onInvalid={analytics.onInvalid} className="ph-no-capture flex flex-col gap-6 rounded-3xl bg-white p-6 text-stone-900 shadow-2xl md:p-8" noValidate={!onLastStep} onSubmit={onSubmit}>
      <WizardProgress copy={copy} step={step} />
      <header className="flex flex-col gap-1">
        <h2 className="font-christmas text-3xl font-bold text-red-800 outline-none" ref={heading} tabIndex={-1}>{stepCopy.title}</h2>
        <p className="text-stone-600">{stepCopy.lead}</p>
      </header>
      <StepFields consent={consent} copy={copy} onConsent={setConsent} priceLabel={priceLabel} privacyHref={privacyHref} set={set} step={step} storyProps={props} values={values} />
      <FormAlert message={stepErrorMessage(copy, stepError)} />
      <FormAlert message={checkout.error} />
      <WizardNav
        backLabel={copy.back}
        disabled={onLastStep && !canPay(consent, checkout)}
        label={submitLabel(copy, onLastStep, checkout.submitting)}
        onBack={step > 0 ? () => goTo((step - 1) as WizardStep) : undefined}
      />
    </form>
  )
}

function StepFields({ step, storyProps, consent, onConsent, priceLabel, privacyHref, ...fields }: StepFieldsProps & {
  step: WizardStep; storyProps: StoryProps; consent: boolean; onConsent: (value: boolean) => void; priceLabel: string; privacyHref: string
}) {
  if (step === 0) return <ChildFields {...fields} />
  if (step === 1) return <KnowledgeStep set={fields.set} storyProps={storyProps} values={fields.values} />
  return <DeliveryFields {...fields} consent={consent} onConsent={onConsent} priceLabel={priceLabel} privacyHref={privacyHref} />
}

function stepErrorMessage(copy: OrderFormCopy, error: StepError | null): string | null {
  return error ? copy.errors[error] : null
}

/** Sin consentimiento, con un envío en curso o durante el `Retry-After`, el botón no paga. */
function canPay(consent: boolean, checkout: { submitting: boolean; locked: boolean }): boolean {
  return consent && !checkout.submitting && !checkout.locked
}

function submitLabel(copy: OrderFormCopy, onLastStep: boolean, submitting: boolean): string {
  if (!onLastStep) return copy.next
  return submitting ? copy.submitting : copy.submit
}

function FormAlert({ message }: { message: string | null }) {
  if (!message) return null
  return <p className="rounded-md bg-red-50 p-3 text-sm text-red-800" role="alert">{message}</p>
}

function WizardProgress({ copy, step }: { copy: OrderFormCopy; step: WizardStep }) {
  const total = LAST_STEP + 1
  const label = `${copy.stepLabel} ${step + 1} ${copy.ofLabel} ${total}`
  return (
    <div className="flex items-center gap-3">
      <div aria-label={label} aria-valuemax={total} aria-valuemin={1} aria-valuenow={step + 1} className="h-2 flex-1 overflow-hidden rounded-full bg-stone-200" role="progressbar">
        <div className="h-full rounded-full bg-red-700 transition-[width]" style={{ width: `${((step + 1) / total) * 100}%` }} />
      </div>
      <span className="text-sm font-medium text-stone-600">{label}</span>
    </div>
  )
}

function WizardNav({ label, backLabel, onBack, disabled }: { label: string; backLabel: string; onBack?: () => void; disabled: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      {onBack ? <Button className="text-stone-700" onClick={onBack} type="button" variant="ghost">{backLabel}</Button> : <span />}
      <Button className="rounded-full bg-red-700 px-8 text-white hover:bg-red-800" disabled={disabled} size="lg" type="submit">{label}</Button>
    </div>
  )
}
